import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useTranslation } from "react-i18next"
import { Box, Typography, styled } from "@mui/material"

import { withDebtPayments } from "hocs/features"
import { formatMoney, formatDate } from "helpers/formatters"
import Card from "components/ui/CardAccordion"
import Table from "components/ui/Table"
import {
  MONEY_TYPE,
  STRING_TYPE,
  DATE_TYPE,
  STRING_TRUNCATED_TYPE,
} from "config"
import {
  DebtPaymentListType,
  DebtPaymentType,
  DebtPaymentBindingType,
} from "types"

type Props = {
  payments: DebtPaymentListType
  total: number
}

const InvoicesComponent = ({ payments, total }: Props) => {
  const { t } = useTranslation()

  const isEmpty = total === 0

  const HEADER = [
    {
      label: t("features.debt_payment.table.date"),
      field: "date",
      format: DATE_TYPE,
      minWidth: 70,
    },
    {
      label: t("features.debt_payment.table.payer_name"),
      field: "payer_name",
      format: STRING_TRUNCATED_TYPE,
      minWidth: 130,
    },
    {
      label: t("features.debt_payment.table.reference_number"),
      field: "reference_number",
      format: STRING_TYPE,
      minWidth: 100,
    },
    {
      label: t("features.debt_payment.table.amount"),
      field: "amount",
      format: MONEY_TYPE,
      minWidth: 100,
    },
    {
      label: t("features.debt_payment.table.type"),
      field: "type",
      format: STRING_TYPE,
      minWidth: 100,
    },
  ]

  return (
    <Container data-testid="claim-payments-list">
      {isEmpty ? (
        <Typography component="span" variant="subtitle1">
          {t("pages.payments.empty.title")}
        </Typography>
      ) : (
        <>
          {payments.map((item: DebtPaymentType, index: number) => (
            <Card
              sx={{ width: "100%" }}
              key={index}
              keepHeader={true}
              collapsed={
                <CollapsedContainer>
                  <Title variant="h3">{item.reference_number}</Title>
                  <Date variant="h4">{formatDate(item.date)}</Date>
                  <Balance variant="h4">{formatMoney(item.amount)}</Balance>
                </CollapsedContainer>
              }
              expanded={
                <Table
                  sx={{ maxHeight: 460, marginBottom: "26px" }}
                  header={HEADER}
                  items={item.debt_payment_bindings.map(
                    (payment: DebtPaymentBindingType) => {
                      return {
                        ...payment,
                        type: t(
                          `features.debt_payment.type.${payment.binding_type}.title`,
                        ),
                      }
                    },
                  )}
                />
              }
            />
          ))}
        </>
      )}
    </Container>
  )
}

export default compose<ComponentType>(withDebtPayments)(InvoicesComponent)

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  margin: "42px 0 8px 0",
  [theme.breakpoints.down("lg")]: {
    flexWrap: "wrap",
    flexDirection: "column",
  },
}))

const CollapsedContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexGrow: 1,
  justifyContent: "space-between",
  margin: "0 20px 0 0",
  padding: 0,
}))

const Title = styled(Typography)(({ theme }) => ({
  flexBasis: "40%",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.125rem",
  },
}))

export const Balance = styled(Typography)(({ theme }) => ({
  flexBasis: "30%",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.85rem",
  },
}))

export const Date = styled(Typography)(({ theme }) => ({
  flexBasis: "30%",
  [theme.breakpoints.down("md")]: {
    fontSize: "0.85rem",
  },
}))
